import { css, html, LitElement } from 'lit';

const NAME_KEY = 'bracket.biz:name';
export const CODE_KEY = 'bracket.biz:roomCode';

export class BizWelcome extends LitElement {
  static properties = {
    _name: { state: true },
    _code: { state: true },
    _error: { state: true },
  };

  declare _name: string;
  declare _code: string;
  declare _error: string;

  constructor() {
    super();
    this._name = localStorage.getItem(NAME_KEY) ?? '';
    this._code = sessionStorage.getItem(CODE_KEY) ?? '';
    this._error = '';
  }

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.5rem;
      padding: 4rem 1rem;
      min-height: 100vh;
    }
    h1 {
      font-size: 2.4rem;
      margin: 0;
      letter-spacing: -0.02em;
    }
    .tagline {
      color: var(--fg-dim, #a0a4ad);
      max-width: 28em;
      text-align: center;
      margin-top: -1rem;
    }
    .form {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      width: min(100%, 22rem);
    }
    .row {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }
    .row label {
      font-size: 0.85rem;
      color: var(--fg-dim, #a0a4ad);
    }
    .row input {
      width: 100%;
    }
    .buttons {
      display: flex;
      gap: 0.5rem;
      margin-top: 0.5rem;
    }
    .buttons button {
      flex: 1;
    }
    .error {
      color: var(--danger, #e94f4f);
      font-size: 0.9rem;
      min-height: 1.2em;
    }
  `;

  render() {
    return html`
      <link rel="stylesheet" href="/static/style.css" />
      <h1>bracket.biz</h1>
      <p class="tagline">
        Type a response. Vote on someone else's. Whoever's funniest wins.
      </p>
      <form class="form" @submit="${(e: SubmitEvent) => this.#onJoin(e)}">
        <div class="row">
          <label for="name">Your name</label>
          <input
            id="name"
            type="text"
            maxlength="24"
            .value="${this._name}"
            @input="${(e: Event) => this.#setName((e.target as HTMLInputElement).value)}"
            required
          />
        </div>
        <div class="row">
          <label for="code">Room code (to join an existing lobby)</label>
          <input
            id="code"
            type="text"
            maxlength="4"
            autocapitalize="characters"
            .value="${this._code}"
            @input="${(e: Event) => this.#setCode((e.target as HTMLInputElement).value)}"
            placeholder="ABCD"
          />
        </div>
        <div class="buttons">
          <button type="submit" ?disabled="${!this._canJoin}">Join lobby</button>
          <button
            type="button"
            class="secondary"
            ?disabled="${!this._canCreate}"
            @click="${() => this.#onCreate()}"
          >
            New lobby
          </button>
        </div>
        <div class="error">${this._error}</div>
      </form>
    `;
  }

  get _canJoin(): boolean {
    return this._name.trim().length > 0 && this._code.trim().length === 4;
  }

  get _canCreate(): boolean {
    return this._name.trim().length > 0;
  }

  #setName(v: string) {
    this._name = v;
    localStorage.setItem(NAME_KEY, v);
  }

  #setCode(v: string) {
    const upper = v.toUpperCase();
    this._code = upper;
    sessionStorage.setItem(CODE_KEY, upper);
  }

  #onJoin(e: SubmitEvent) {
    e.preventDefault();
    this._error = '';
    if (!this._canJoin) return;
    this.dispatchEvent(
      new CustomEvent('biz-join', {
        detail: { name: this._name.trim(), code: this._code.trim() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  #onCreate() {
    this._error = '';
    if (!this._canCreate) return;
    this.dispatchEvent(
      new CustomEvent('biz-create', {
        detail: { name: this._name.trim() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  showError(msg: string): void {
    this._error = msg;
  }
}

customElements.define('biz-welcome', BizWelcome);
